import { createUnit } from '../i18n/unitQtyWrapper';
import { getLayerUnits } from '../config/legendUnits';
import { LOCALE_GROUPS } from '../constants/units';
import { swapComma } from './swapComma';

// This function checks to see if localized value needs to take into account crop nominal weight
// when process yield values
export function isLocaleGroupYield(localGroup) {
  return [
    LOCALE_GROUPS.YIELD
  ].includes(localGroup);
}
/**
 * Converts a value using TCC's unit-qty library
 * @param {Number} value
 * @param {Object} units
 * @param {String} units.key
 * @param {String} [units.localeGroup]
 * @param {Number} [units.cropNominalWeight]
 * @param {Object} [opts]
 * @param {Function} [opts.mathOperation]
 * @param {String} [opts.cropCode]
 * @return {Object}
 */

// eslint-disable-next-line no-unused-vars
export function localizeUnits(value, units, { mathOperation, cropCode } = {}) {
  let result = createUnit(value, units.key);
  // https://climate.jira.com/browse/YAN-722
  // NOTE | For crop yields, it is not guaranteed that units.cropNominalWeight will always exist.

  // the below ternary is complicated and not sure of it origins but basically adds the crop nominal weight
  // to the unit object to be used in downstream processing via i18n. This downstream processing breaks without
  // crop nominal weight supplied and throughout the react lifecycle the, see above comment too, the crop nominal
  // weight is not be there. The ternary in the condition check is the logic that keeps this from happening. 
  if (
    units.localeGroup &&
    (isLocaleGroupYield(units.localeGroup) ? units.cropNominalWeight : true)
  ) {
    result = result.forLocaleGroup(
      units.localeGroup,
      units.cropNominalWeight,
      mathOperation,
      { cropCode, moisture: units.moisture, dryMatterYieldUsage: units.dryMatterYieldUsage }
    );
  }
  return {
    q: result.scalarLocaleString,
    u: units.unitType
      ? result.label.quantitySpecific
      : result.symbol.quantitySpecific,
    uSingular: result.unitKey,
  };
}


/**
 * Converts the values within intervals using TCC's unit-qty library
 * @param {Array} intervals
 * @param {Object} units
 * @param {String} units.key
 * @param {String} [units.localeGroup]
 * @param {Object} [units.to]
 * @param {Number} [units.cropNominalWeight]
 * @param {Object} [opts]
 * @param {String} [opts.cropCode]
 * @return {Object} - Converted intervals and unit key
 */
export function localizeIntervals(intervals, units, { cropCode } = {}) {
  let key;
  let singularKey;
  const finalIntervals = intervals.map((interval, index) => {
    let mathOperation;
    if (index === 0) {
      mathOperation = Math.floor;
    }
    if (index === intervals.length - 1) {
      mathOperation = Math.ceil;
    }

    // If the interval has `stringValue` instead of `value`, we assume it's value is 0.
    const localVal = localizeUnits(interval.value || 0, units, {
      mathOperation,
      cropCode,
    });
    key = key || localVal.u;
    singularKey = singularKey || localVal.uSingular;

    return {
      ...interval,
      value: localVal.q,
    };
  });

  return {
    cropNominalWeight: units.cropNominalWeight,
    intervals: finalIntervals,
    key,
    singularKey,
  };
}

/**
 * Converts intervals to locale's date formate
 * @param {Array} intervals
 * @param {String} locale
 * @return {Array}
 */
function localizeDates(intervals, locale) {
  const dateOpts = { month: '2-digit', year: 'numeric', day: '2-digit' };
  return intervals.map((interval) => ({
    ...interval,
    stringValue: new Date(interval.stringValue).toLocaleDateString(
      locale,
      dateOpts
    ),
  }));
}

/**
 * Converts or rounds the values within intervals if possible
 * @param {Array} [intervals=[]]
 * @param {Object} [units={}]
 * @param {String} [locale]
 * @param {String} [cropCode]
 * @return {Object} - Processed intervals and units key
 */
export function processIntervals(intervals = [], units = {}, locale, cropCode) {
  // If key is undefined, intervals are strings/numbers. No need for rounding or conversion.
  if (!units.key) {
    switch (units.processSpecial) {
      case 'date':
        return {
          intervals: localizeDates(intervals, locale),
        };

      default:
        // There might be some cases like CEC, where no unit is associated to the measurement
        // but we still might want to allow the user edit the legend, for this purpose we need
        // the numbers are casted to "strings"
        return {
          intervals: intervals.map(({ value, ...attrs }) => ({
            ...(value ? { value: String(value) } : {}),
            ...attrs,
          })),
        };
    }
  }
  // If key is defined, convert intervals based on user's locale
  return localizeIntervals(intervals, units, { cropCode });
}

/**
 * Convert legend values back to metric before they are sent in PUT request
 * @param {Object} legend
 * @param {Number} legend.maxValue
 * @param {Number} legend.minValue
 * @param {Object} units
 * @param {String} units.singularKey
 * @param {String} layer
 * @param {String} localeGroup
 * @return {Object}
 */
export function convertLegendValues(legend, units, layer, localeGroup) {
  const { maxValue, minValue, steps } = legend;
  const formattedMax = Number.isNaN(Number(maxValue))
    ? swapComma(maxValue)
    : maxValue;
  const formattedMin = Number.isNaN(Number(minValue))
    ? swapComma(minValue)
    : minValue;

  // Check if the current unit is the expected metric unit for the given map layer
  const expectedUnit = getLayerUnits(layer, localeGroup);
  if (expectedUnit) {
    const expectedUnitKey = expectedUnit.key;
    const isExpectedUnit = units.singularKey === expectedUnitKey;

    const max = isExpectedUnit
      ? formattedMax
      : createUnit(formattedMax, units.singularKey).to(expectedUnitKey, units)
        .scalar;
    const min = isExpectedUnit
      ? formattedMin
      : createUnit(formattedMin, units.singularKey).to(expectedUnitKey, units)
        .scalar;

    return { max, min, steps, unit: expectedUnitKey };
  }

  // If there is no unit we return the raw numbers. This case might be triggered by
  // allowing the edit of a legend without units.
  return {
    max: formattedMax,
    min: formattedMin,
    steps,
    unit: '',
  };
}

/**
 * Get the crop's nominal weight from cropList
 * @param {String} [crop]
 * @param {Object} cropList
 * @return {Number}
 */
export function getCropNominalWeight(crop, cropList) {
  const cropInfo = crop && cropList[crop];
  if (cropInfo) {
    return (
      cropInfo.customNominalWeight ||
      (cropInfo['nominal-weight'] && cropInfo['nominal-weight'].q)
    );
  }

  return null;
}
